<template>
  <div>
    <div class="FormBuilder-container Content">
      <div
        class="u-flex"
        style="margin-bottom: 55px; justify-content: space-between"
      >
        <div>
          <h2 class="PageHeader">
            {{ event.name }}
          </h2>

          <p>{{ event.last_update_at }}</p>
        </div>
        <preview-form-button />
      </div>

      <theme-button @toggle-theme-builder="toggleThemeBuilder" />

      <div class="SectionContainer u-flex">
        <section :class="[{'is-loading': ui.isLoading }, 'SectionContainer-left']">
          <static-form-renderer
            @web-form-renderer:replace="handleReplaceQuestion"
          />
        </section>
        <section
          class="QuestionBuilder SectionContainer-right"
          :style="`height:${ui.questionBuilderHeight}`"
        >
          <theme-builder
            v-click-outside="toggleThemeBuilder"
            :show-theme-builder="ui.showThemeBuilder"
          />

          <header-creation-steps
            selected="Form Builder"
            :event="event"
          />

          <question-list
            :question-to-replace="questionToReplace"
            @question-list:select-question="handleSelectQuestionToReplace"
            @question-list:cancel-replace-question="questionToReplace.from = null"
          />
          <template v-if="selectedQuestion.question_uuid && questionToReplace.from === null">
            <question-builder
              :key="selectedQuestion.index"
            />
          </template>
          <replace-question-modal
            v-model="ui.showReplaceModal"
            :question-from="questionToReplace.from"
            :question-to="questionToReplace.to"
            @replace-question:submit="handleReplaceNewQuestionSubmit"
          />
        </section>
      </div>
      <footer-creation-buttons
        v-if="isEventCreationRoute"
        class="FooterActionButtons"
      >
        <template #left-aligned-buttons>
          <router-link :to="{ name: 'basic-info', params: { id: $route.params.id }}">
            <button class="Button Button--green-outline">
              Back
            </button>
          </router-link>
        </template>
        <template #right-aligned-buttons>
          <button
            :class="[{'is-loading': ui.saveLoading }, 'Button Button--blue']"
            :disabled="formQuestions.length === 0 || selectedQuestion.id !== null || ui.disableSaveButtons"
            @click="handleSaveEvent(false)"
          >
            Save
          </button>
          <button
            :class="[{'is-loading': ui.submitLoading }, 'Button Button--green']"
            :disabled="formQuestions.length === 0 || selectedQuestion.id !== null || ui.disableSaveButtons"
            @click="handleSaveEvent"
          >
            Save & Continue
          </button>
        </template>
      </footer-creation-buttons>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import HeaderCreationSteps from '../common/header-creation-steps.vue';
import FooterCreationButtons from '../common/footer-creation-buttons.vue';
import QuestionBuilder from '../question-builder/question-builder.vue';
import StaticFormRenderer from './static-form-renderer';
import ReplaceQuestionModal from '../modals/replace-question';
import QuestionList from '../question-builder/question-list.vue';
import PreviewFormButton from '../common/preview-form-button';
import ThemeButton from '../theme-builder/theme-button.vue';
import ThemeBuilder from '../theme-builder/theme-builder.vue';

export default {
  components: {
    HeaderCreationSteps,
    FooterCreationButtons,
    QuestionBuilder,
    StaticFormRenderer,
    ReplaceQuestionModal,
    QuestionList,
    PreviewFormButton,
    ThemeButton,
    ThemeBuilder,
  },
  data() {
    return {
      questionToReplace: {
        from: null,
        to: null,
      },
      ui: {
        isLoading: false,
        saveLoading: false,
        submitLoading: false,
        showReplaceModal: false,
        brandImageSelected: false,
        questionBuilderHeight: 'auto',
        disableSaveButtons: false,
        showThemeBuilder: false,
      },
    };
  },
  computed: {
    ...mapState('events', {
      event: (state) => state.event,
    }),
    ...mapState('forms', {
      selectedQuestion: (state) => state.selectedQuestion,
      formQuestions: (state) => state.form.questions,
    }),
    isEventCreationRoute() {
      return this.$route.path.indexOf('create-event') > -1;
    },
  },
  created() {
    window.addEventListener('scroll', this.handleScroll);
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  mounted() {
    window.scrollTo(0, 0);

    if (this.$route.params.id !== undefined) {
      this.ui.isLoading = true;

      this.getEvent(this.$route.params.id)
        .then(() => {
          if (this.event.is_locked) {
            this.$router.push({ name: 'event-records', params: { eventId: this.event.id } });
          } else {
            this.ui.isLoading = false;
          }
        })
        .then(
          this.getThemes(this.$route.params.id),
        )
        .catch(({ data }) => {
          this.ui.isLoading = false;

          this.$bus.$emit('displayMsg', {
            text: data.message,
            type: 'warning',
            duration: 5000,
          });
        });
    }
  },
  methods: {
    ...mapActions('events', [
      'getEvent',
      'updateEvent',
    ]),
    ...mapMutations('forms', [
      'updateSelectedQuestion',
      'resetSelectedQuestion',
      'setFormQuestions',
    ]),
    ...mapActions('themes', ['getThemes']),

    handleScroll() {
      // Set container height to enable question list to remain sticky - scroll down the page pinned to top
      this.ui.questionBuilderHeight = `${document.querySelector('.SectionContainer-left').clientHeight}px`;
    },

    handleSaveEvent(shouldNavigateToNextStage = true) {
      if (shouldNavigateToNextStage) {
        this.ui.submitLoading = true;
      } else {
        this.ui.saveLoading = true;
      }

      const questions = this.getSanitizedFormInputsForSubmission();

      this.updateEvent({ ...this.event, questions })
        .then((data) => {
          if (shouldNavigateToNextStage) {
            this.ui.submitLoading = false;
            this.$router.push({ name: 'messages', params: { id: this.event.id } });
          } else {
            this.ui.saveLoading = false;
            this.setFormQuestions(data.questions);
            this.indexQuestions();

            if (data.is_locked) {
              this.ui.disableSaveButtons = true;

              this.$bus.$emit('displayMsg', {
                text: 'The event is locked and because records are currently being reprocessed. Please check back soon.',
                type: 'error',
                duration: 25000,
              });

              setTimeout(() => {
                this.$router.push({ name: 'event-records', params: { eventId: this.event.id } });
              }, 2000);
              return;
            }

            this.$bus.$emit('displayMsg', {
              text: 'Form saved',
              type: 'success',
            });
          }
        })
        .catch(({ data }) => {
          // Important to reindex questions for drag and drop and selection functionality.
          // this.setFormQuestions(data.questions);
          this.indexQuestions();

          if (shouldNavigateToNextStage) {
            this.ui.submitLoading = false;
          } else {
            this.ui.saveLoading = false;
          }
          const message = data.meta !== undefined && data.meta.length ? data.meta[0].message : data.data.message;
          this.$bus.$emit('displayMsg', {
            text: message,
            type: 'error',
            duration: 25000,
          });

          if (data.type === 'Locked') {
            this.ui.disableSaveButtons = true;
            setTimeout(() => {
              this.$router.push({ name: 'event-records', params: { eventId: this.event.id } });
            }, 2000);
          }
        });
    },

    handleReplaceQuestion(question) {
      if (this.questionToReplace.from === null) {
        this.questionToReplace.from = question;
      } else {
        this.questionToReplace = {
          to: null,
          from: null,
        };
      }
    },

    getSanitizedFormInputsForSubmission() {
      const questions = [...this.formQuestions];

      questions.forEach((question) => {
        // Custom logic for checkboxes
        // Backend expects question_uuid checkboxOptGroup for checkbox custom quesitons with options
        if (question.is_custom_question && question.question_uuid === 'checkbox' && question.options !== null) {
          question.question_uuid = 'checkboxOptGroup';// eslint-disable-line
        }
        delete question.index;// eslint-disable-line
      });

      return questions;
    },

    indexQuestions() {
      const indexedQuestions = this.formQuestions.map((q, index) => ({ ...q, index }));
      this.setFormQuestions(indexedQuestions);
    },

    handleReplaceNewQuestionSubmit() {
      const indexOfQuestionToBeReplaced = this.formQuestions.findIndex((question) => question.index === this.questionToReplace.from.index);
      const questions = [...this.formQuestions];
      questions[indexOfQuestionToBeReplaced] = this.questionToReplace.to;

      // If the question has an id then it means it has been saved in the database, otherwise replacing a question has no impact.
      if (this.questionToReplace.from.id !== undefined) {
        const { label, question_uuid, html_form_input_id } = this.questionToReplace.from; //eslint-disable-line

        questions[indexOfQuestionToBeReplaced].replaced_question_details = {
          label,
          question_uuid, //eslint-disable-line
          html_form_input_id, //eslint-disable-line
        };
      }

      this.setFormQuestions(questions);
      this.updateSelectedQuestion(questions[indexOfQuestionToBeReplaced]);

      this.ui.showReplaceModal = false;
      this.questionToReplace = {
        from: null,
        to: null,
      };
    },

    handleSelectQuestionToReplace(question) {
      if (this.questionToReplace.from !== null) {
        this.questionToReplace.to = question;
        this.ui.showReplaceModal = true;
      }
    },

    handleupdateSelectedQuestion(question = null) {
      // this.selectedQuestion = question;
      this.updateSelectedQuestion(question);
    },

    toggleThemeBuilder() {
      this.ui.showThemeBuilder = !this.ui.showThemeBuilder;
    },
  },
};
</script>

<style lang="less" scoped>

.QuestionBuilder {
  height: 100vh;
  padding: 0;
}

.SectionContainer-left {
  background: #F7F8F9 !important;
}
.SectionContainer-right {
  position: relative;
}

.sortable-ghost {
  box-shadow: none;
  border: 2px dotted #046FF0;
  background: transparent;
}

</style>
